import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Configuration options for the useResendTimer hook
 */
interface UseResendTimerOptions {
  /** Initial countdown time in seconds (default: 30) */
  initialTime?: number;
  /** Callback function executed when timer reaches 0 */
  onTimerEnd?: () => void;
}

/**
 * Return value object from the useResendTimer hook
 */
interface UseResendTimerReturn {
  /** Remaining time in seconds until resend is allowed */
  timeRemaining: number;
  /** Whether the timer is currently active/counting down */
  isActive: boolean;
  /** Whether the user can resend (timer is 0 and not active) */
  canResend: boolean;
  /** Function to start/restart the countdown timer */
  startTimer: () => void;
  /** Function to reset and stop the timer */
  resetTimer: () => void;
}

/**
 * Custom hook for managing MFA resend timer
 *
 * Manages a countdown timer for the MFA code resend functionality.
 * When timer is active, the resend button should be disabled.
 * When timer reaches 0, the resend button becomes enabled.
 *
 * ## State Machine Diagram
 *
 * ```mermaid
 * stateDiagram-v2
 * [*] --> Idle
 * Idle --> Active: startTimer
 * Active --> Counting: timer starts
 * Counting --> Counting: decrement
 * Counting --> TimerEnd: reaches zero
 * TimerEnd --> Idle: onTimerEnd
 * Active --> Idle: resetTimer
 * Counting --> Idle: resetTimer
 * Idle --> [*]
 * ```
 *
 * ## Flow Diagram
 *
 * ```mermaid
 * flowchart TD
 * A[Mount] --> B[Initialize]
 * B --> C[Render]
 * C --> D{Action}
 * D -->|Resend| E{Can Resend}
 * E -->|Yes| F[Start Timer]
 * E -->|No| G[Disabled]
 * F --> H[Reset Time]
 * H --> I[Activate]
 * I --> J[Start Interval]
 * J --> K[Countdown]
 * K --> L[Decrement]
 * L --> M{Time Left}
 * M -->|Yes| N[Update]
 * N --> K
 * M -->|No| O[Clear]
 * O --> P[Deactivate]
 * P --> Q[Callback]
 * Q --> R[Set Zero]
 * R --> C
 * D -->|Reset| S[Reset]
 * S --> T[Clear]
 * T --> U[Reset All]
 * U --> C
 * G --> C
 * C --> V{Unmount}
 * V -->|No| D
 * V -->|Yes| W[Cleanup]
 * W --> X[End]
 * ```
 *
 * ## Sequence Diagram
 *
 * ```mermaid
 * sequenceDiagram
 * actor User
 * participant Component
 * participant Hook
 * participant Timer
 * User->>Component: Mount
 * Component->>Hook: Initialize
 * Hook-->>Component: canResend true
 * Component->>User: Show enabled
 * User->>Component: Click resend
 * Component->>Hook: startTimer
 * Hook->>Timer: Start
 * Hook-->>Component: canResend false
 * Component->>User: Show disabled
 * loop Every second
 * Timer->>Hook: Tick
 * Hook-->>Component: Update time
 * end
 * Timer->>Hook: Final tick
 * Hook->>Timer: Clear
 * Hook->>Hook: onTimerEnd
 * Hook-->>Component: canResend true
 * Component->>User: Show enabled
 * ```
 *
 * ## Data Structure
 *
 * ```mermaid
 * classDiagram
 * class Options {
 * +number initialTime
 * +Function onTimerEnd
 * }
 * class Return {
 * +number timeRemaining
 * +boolean isActive
 * +boolean canResend
 * +Function startTimer
 * +Function resetTimer
 * }
 * class State {
 * -number timeRemaining
 * -boolean isActive
 * -Timeout intervalRef
 * }
 * Options --> State
 * State --> Return
 * ```
 *
 * @param options - Configuration options
 * @param options.initialTime - Initial timer value in seconds (default: 30)
 * @param options.onTimerEnd - Callback function called when timer reaches 0
 *
 * @returns Object with timer state and control functions
 *
 * @example
 * Basic usage with MFA code resend
 * ```tsx
 * const { timeRemaining, canResend, startTimer } = useResendTimer({
 *   initialTime: 30,
 *   onTimerEnd: () => console.log('Can resend now')
 * });
 *
 * // Start timer when MFA code is sent
 * useEffect(() => {
 *   startTimer();
 * }, []);
 *
 * // Use in JSX
 * <Button disabled={!canResend}>
 *   {canResend ? 'Resend Code' : `Resend in ${timeRemaining}s`}
 * </Button>
 * ```
 *
 * @example
 * Advanced usage with custom initial time and callback
 * ```tsx
 * const MFAForm = () => {
 *   const { timeRemaining, canResend, startTimer, resetTimer } = useResendTimer({
 *     initialTime: 60,
 *     onTimerEnd: () => {
 *       notification.info({ message: 'You can now resend the code' });
 *     }
 *   });
 *
 *   const handleResend = async () => {
 *     await resendMFACode();
 *     startTimer();
 *     message.success('Code sent!');
 *   };
 *
 *   return (
 *     <Space>
 *       <Button onClick={handleResend} disabled={!canResend}>
 *         {canResend ? 'Resend' : `Wait ${timeRemaining}s`}
 *       </Button>
 *       <Button onClick={resetTimer}>Cancel</Button>
 *     </Space>
 *   );
 * };
 * ```
 */
export const useResendTimer = ({
  initialTime = 30,
  onTimerEnd,
}: UseResendTimerOptions = {}): UseResendTimerReturn => {
  const [timeRemaining, setTimeRemaining] = useState(initialTime);
  const [isActive, setIsActive] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const canResend = timeRemaining === 0 && !isActive;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    setTimeRemaining(initialTime);
    setIsActive(true);
  }, [initialTime]);

  const resetTimer = useCallback(() => {
    clearTimer();
    setTimeRemaining(initialTime);
    setIsActive(false);
  }, [initialTime, clearTimer]);

  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearTimer();
            setIsActive(false);
            onTimerEnd?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      clearTimer();
    };
  }, [isActive, timeRemaining, clearTimer, onTimerEnd]);

  return {
    timeRemaining,
    isActive,
    canResend,
    startTimer,
    resetTimer,
  };
};

export default useResendTimer;
